/**
 * One-stop builder for the `file` prop handed to react-pdf's `<Document>`,
 * plus the viewer-chrome colors `<PdfReader>` paints itself with.
 *
 * Ported verbatim from xop-edge-staff's `dashboard-shared`, for the same
 * reason `shareChat.ts` and `verticals.ts` are: the CRM copied into
 * `apps/web/src/crm` — and now `components/pdf/pdf-reader.tsx`, the command
 * center's own inline PDF reader — import these symbols from there. Carrying
 * five small pure values is cheaper than a package boundary between repos.
 *
 * Why `toPdfFileData` exists (the bug it fixes): pdf.js TRANSFERS the bytes you
 * give it to its Web Worker (`postMessage` with a transfer list), which
 * DETACHES the underlying `ArrayBuffer` in the main thread. The naive prop
 * `{ data: new Uint8Array(bytes) }` is a VIEW over the SAME buffer, not a copy,
 * so pdf.js detached the caller's own `bytes` (typically a react-query cache
 * entry). The NEXT read of that detached buffer threw:
 *   • JavaScriptCore (macOS WKWebView): "TypeError: Buffer is already detached"
 *   • V8 (Chrome / web): "Cannot perform Construct on a detached ArrayBuffer"
 * which, with no React error boundary, blanked the whole page.
 *
 * The fix: hand pdf.js a fresh COPY each time, so it only ever detaches its own
 * throwaway buffer and the caller's `bytes` stay intact and re-renderable. This
 * portal is web-only, so it never saw the macOS desktop crash — but it hits the
 * same detach on any remount of cached bytes, and an invoice preview that
 * blanks on the second visit is not something to re-derive later.
 */

/**
 * Thrown by {@link toPdfFileData} when `bytes` is already detached (or empty) —
 * i.e. there is nothing to copy. Callers catch this and degrade to a Download
 * fallback instead of letting the raw engine TypeError blank the app.
 */
export class PdfBytesDetachedError extends Error {
  constructor() {
    super("PDF bytes are detached or empty and cannot be rendered");
    this.name = "PdfBytesDetachedError";
  }
}

/**
 * Build react-pdf's `{ data }` file prop from `bytes`, backed by a fresh COPY
 * so pdf.js can transfer/detach it without touching the source.
 *
 * @throws {PdfBytesDetachedError} if `bytes` is detached or empty.
 */
export function toPdfFileData(bytes: ArrayBuffer): { data: Uint8Array } {
  // A detached ArrayBuffer reports `byteLength === 0` (it does NOT throw on the
  // property access). Checking this BEFORE we touch the bytes means we never
  // hit the raw "Buffer is already detached" TypeError — we surface our own
  // typed error so the component can show the Download fallback. A genuinely
  // empty buffer is equally un-renderable, so it takes the same path.
  if (bytes.byteLength === 0) throw new PdfBytesDetachedError();
  // `ArrayBuffer.prototype.slice` COPIES into a brand-new buffer that pdf.js is
  // free to transfer/detach; the source `bytes` is left fully intact.
  return { data: new Uint8Array(bytes.slice(0)) };
}

/**
 * The Chrome / Google-style **dark mat** behind an inline PDF's white page
 * sheets. A deliberate, theme-INDEPENDENT product color (like the `#fff` sheet
 * itself): white pages read as paper on a dark mat in both light and dark app
 * themes. Shared so the ONE app-wide reader (`components/pdf/pdf-reader.tsx`,
 * `<PdfReader>`) shows the identical viewer everywhere.
 */
export const PDF_VIEWER_MAT_BG = "#3a3d42";

/**
 * Drop shadow that lifts each white sheet off {@link PDF_VIEWER_MAT_BG}. A raw
 * shadow (NOT `theme.shadows`, which is tuned for light surfaces and vanishes on
 * a dark background) — the signature Chrome/Google PDF-reader look.
 */
export const PDF_SHEET_SHADOW = "0 2px 10px rgba(0,0,0,0.45)";

/**
 * The remaining raw viewer-CHROME colors for `<PdfReader>`: the white paper
 * sheet ({@link PDF_CHROME_LIGHT}) and the black base darkening the toolbar
 * ({@link PDF_CHROME_DARK}). They are theme-INDEPENDENT product colors — the
 * reader is a self-contained product surface with its own palette, like a
 * canvas/map fill. Layer opacity over them with MUI's `alpha()` at the call
 * site (e.g. `alpha(PDF_CHROME_LIGHT, 0.85)` for a muted white icon on the dark
 * toolbar).
 */
export const PDF_CHROME_LIGHT = "#fff";
export const PDF_CHROME_DARK = "#000";
